// src/frontend/src/pages/Scenarios.tsx - Redesigned as Scenario Library
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import ScenarioSelector from '../components/Simulation/ScenarioSelector';
import { SimulationScenario } from '../types/simulation';
import { conversationService } from '../api/simulationApi';

const Scenarios: React.FC = () => {
  const navigate = useNavigate();
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');

  const handleSelectScenario = async (scenario: SimulationScenario) => {
    try {
      setIsStarting(true);
      setError('');
      
      // Start a conversation with this scenario
      const response = await conversationService.startConversation({ 
        scenarioId: scenario._id 
      });
      
      // Navigate to the conversation page
      navigate(`/coaching-sessions/${response.data.data._id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start coaching session');
      setIsStarting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Browse Scenarios</h1>
              <p className="text-gray-600 text-lg">
                Search and filter through our comprehensive library of stakeholder conversation scenarios.
              </p>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/learning-tracks')}
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                🎯 Try Learning Tracks
              </button>
              <button
                onClick={() => navigate('/custom-scenarios')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                ✨ Create Custom
              </button>
            </div>
          </div>

          {/* Quick Stats or Featured Categories */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">100+</div>
              <div className="text-sm text-blue-800">Total Scenarios</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">8</div>
              <div className="text-sm text-green-800">Stakeholder Types</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">15</div>
              <div className="text-sm text-purple-800">Categories</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">3</div>
              <div className="text-sm text-orange-800">Difficulty Levels</div>
            </div>
          </div>
        </div>

        {/* Navigation Breadcrumb */}
        <div className="mb-6">
          <nav className="text-sm text-gray-600">
            <span>🏠 Dashboard</span>
            <span className="mx-2">→</span>
            <span className="font-medium text-gray-900">Browse Scenarios</span>
          </nav>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}
        
        {/* Loading State */}
        {isStarting ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-xl">Preparing your coaching session...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Info Section for New Users */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">New to PM Coach?</h3>
                  <p className="text-gray-700 mb-3">
                    For a structured learning experience, try our <strong>Learning Tracks</strong> which provide 
                    progressive scenarios designed for your career stage. Use this library when you want to practice 
                    specific situations or explore advanced scenarios.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate('/learning-tracks')}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      🎯 Start with Learning Tracks
                    </button>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Recommended for beginners
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Scenario Selector */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Find Your Perfect Scenario</h2>
                <div className="text-sm text-gray-500">
                  💡 Tip: Use filters to find scenarios matching your specific learning goals
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Search by difficulty level, stakeholder type, challenge category, or industry focus. 
                Each scenario provides realistic practice with AI-powered stakeholders that adapt to your responses.
              </p>
            </div>

            {/* Enhanced Scenario Selector Component */}
            <ScenarioSelector onSelectScenario={handleSelectScenario} />

            {/* Bottom CTA */}
            <div className="mt-12 bg-gray-50 rounded-xl p-8 border border-gray-200 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Need something more specific?
              </h3>
              <p className="text-gray-600 mb-6">
                Create custom scenarios tailored to your exact situation, industry, or team needs.
              </p>
              <button
                onClick={() => navigate('/custom-scenarios')}
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Create Custom Scenario
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Scenarios;
